import useQueryParams from "utils/useQueryParams";
import SegmentControl from "Components/Sales/SegmentControl";
import { ActivityRecord } from "Components/Analytics/MemberActivity";
import { PaymentMixCard } from "Components/Analytics/PaymentMixCard";
import { LastVisitMembers } from "Components/Analytics/LastVisitMembers";
import { useEffect, useRef } from "react";
import { analyticsParams, displayMember, useAnalytics } from "JS/analyticsAPI";
import "Styles/pages/analytics/analytics.css";
const n = (v) => new Intl.NumberFormat("ko-KR").format(Math.round(v));
const won = (v) => "₩" + n(v);
const pct = (v) => (v === null ? "—" : v.toFixed(1) + "%");
export default function Analytics() {
  useEffect(() => {
    const previous = document.title;
    document.title = "유입·전환율 분석 | 마크픽 관리자";
    return () => {
      document.title = previous;
    };
  }, []);
  const { searchParams, updateQueryParams } = useQueryParams();
  const optionsRequest = useAnalytics("options");
  const options = optionsRequest.data;
  const choice = (key, values, fallback) =>
    values.includes(searchParams.get(key)) ? searchParams.get(key) : fallback;
  const filters = {
    period: choice(
      "period",
      options?.signup_periods.map((p) => p.id) || [],
      "all",
    ),
    window: Number(choice("window", ["7", "30"], "7")),
    service: choice(
      "service",
      options?.services.map((s) => s.label) || [],
      "all",
    ),
    channel: choice(
      "channel",
      options?.channels.map((c) => c.label) || [],
      "all",
    ),
  };
  const tab = choice("tab", ["conversion", "visits"], "conversion");
  const lastChannel = choice(
    "lastChannel",
    options?.channels.map((c) => c.label) || [],
    "all",
  );
  const status = choice(
    "status",
    ["all", "pending", ...(options?.behavior_states.map((s) => s.label) || [])],
    "pending",
  );
  const sort = choice("sort", ["rate", "revenue"], "rate");
  const detailId = /^mp_user_\d+$/.test(searchParams.get("detail") || "")
    ? searchParams.get("detail")
    : null;
  const params = analyticsParams(filters, options);
  const overview = useAnalytics(
    "overview",
    { ...params, channel_sort: sort },
    Boolean(options),
  );
  const detail = useAnalytics(
    `members/${detailId}`,
    { as_of: options?.latest_as_of, observation_days: filters.window },
    Boolean(detailId && options),
  );
  const member = detailId
    ? detail.data?.member.id === detailId
      ? displayMember(detail.data.member, options)
      : { id: detailId, name: "회원" }
    : null;
  const dialog = useRef(null);
  useEffect(() => {
    if (detailId && options && !dialog.current?.open)
      dialog.current?.showModal();
    if (!detailId && dialog.current?.open) dialog.current.close();
  }, [detailId, options]);
  const resetList = {
    search: null,
    lastPage: null,
    page: null,
    member: null,
    detail: null,
    from: null,
    to: null,
  };
  const update = (patch) =>
    updateQueryParams({ ...resetList, ...patch, status: null });
  const updateVisit = (patch) => updateQueryParams({ ...resetList, ...patch });
  const channels = options?.channels.map((c) => c.label) || [];
  const values = (s) => ({
    count: s.signup_members,
    buyers: s.paying_members,
    rate: s.conversion_rate,
    revenue: s.gross_paid_amount,
    perMember: s.revenue_per_member,
  });
  const summary = overview.data
    ? {
        ...values(overview.data.kpis),
        states: overview.data.states.map((s) => ({
          label: s.label,
          count: s.members,
        })),
        funnel: overview.data.funnel.map((s) => ({
          label: s.label,
          count: s.members,
        })),
      }
    : null;
  const channelRows =
    overview.data?.channels.map((c) => ({
      channel:
        options.channels.find((o) => o.id === c.channel)?.label || c.channel,
      ...values(c),
    })) || [];
  const openMember = (m) =>
    updateQueryParams({ detail: m.id, from: null, to: null });
  if (optionsRequest.error)
    return (
      <div className="a2">
        <p role="alert">{optionsRequest.error}</p>
        <button onClick={optionsRequest.retry}>다시 시도</button>
      </div>
    );
  if (!options)
    return (
      <div className="a2" role="status">
        분석 선택지를 불러오는 중…
      </div>
    );
  const snapshot = options.latest_as_of.slice(0, 10);
  return (
    <div className="a2">
      <div className="a2-main">
        <div className="a2-title">
          <div>
            <h1>유입·전환율 분석</h1>
          </div>
          <div className="a2-asof">
            <strong>기준일 {snapshot.replaceAll("-", ".")} · KST</strong>
          </div>
        </div>
        <div className="a2-toolbar">
          <SegmentControl
            aria-label="분석 종류"
            options={[
              { key: "conversion", label: "가입·결제 전환" },
              { key: "visits", label: "마지막 방문 페이지" },
            ]}
            value={tab}
            onChange={(tab) => updateQueryParams({ tab })}
          />
          <div className="a2-filters">
            <label>
              가입 기간
              <select
                value={filters.period}
                onChange={(e) => update({ period: e.target.value })}
              >
                {options.signup_periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              관찰 기간
              <select
                value={filters.window}
                onChange={(e) => update({ window: Number(e.target.value) })}
              >
                <option value={7}>가입 후 7일</option>
                <option value={30}>가입 후 30일</option>
              </select>
            </label>
            <label>
              가입 관심 서비스
              <select
                value={filters.service}
                onChange={(e) => update({ service: e.target.value })}
              >
                <option value="all">전체 서비스</option>
                {options.services
                  .map((o) => o.label)
                  .map((s) => (
                    <option key={s}>{s}</option>
                  ))}
              </select>
            </label>
          </div>
        </div>
        {tab === "conversion" && overview.error && (
          <p role="alert">
            {overview.error} <button onClick={overview.retry}>다시 시도</button>
          </p>
        )}
        {tab === "conversion" && !summary && !overview.error && (
          <p role="status">분석 데이터를 불러오는 중…</p>
        )}
        {tab === "conversion" ? (
          summary && (
            <>
              <div className="a2-context">
                <span>
                  <b>
                    {filters.channel === "all"
                      ? "전체 유입 경로"
                      : filters.channel}
                  </b>{" "}
                  · 관찰 완료 {n(summary.count)}명{" "}
                  <span className="a2-muted">
                    / 관찰 중 {overview.data.cohort.pending}명은 성과 집계 제외
                  </span>
                </span>
                {filters.channel !== "all" && (
                  <button onClick={() => update({ channel: "all" })}>
                    유입 경로 선택 해제 ×
                  </button>
                )}
              </div>
              <section className="a2-kpis" aria-label="선택한 회원의 핵심 성과">
                {[
                  [
                    "가입 회원",
                    n(summary.count) + "명",
                    "관찰 기간을 모두 지난 회원",
                  ],
                  [
                    "결제 회원",
                    n(summary.buyers) + "명",
                    `가입 후 ${filters.window}일 내 중복 제거`,
                  ],
                  [
                    "가입 → 결제율",
                    pct(summary.rate),
                    `${summary.buyers}명 ÷ ${summary.count}명`,
                  ],
                  [
                    "결제금액",
                    won(summary.revenue),
                    "관찰 기간 내 결제 · 환불 전",
                  ],
                  [
                    "가입자당 매출",
                    summary.perMember === null ? "—" : won(summary.perMember),
                    "미결제 회원도 분모에 포함",
                  ],
                ].map(([label, value, note], i) => (
                  <article
                    className={i === 2 ? "a2-kpi a2-highlight" : "a2-kpi"}
                    key={label}
                  >
                    <h2>{label}</h2>
                    <strong>{value}</strong>
                    <p>{note}</p>
                  </article>
                ))}
              </section>
              <div className="a2-grid a2-conversion-grid">
                <section className="a2-panel a2-channels">
                  <div className="a2-panel-head">
                    <div>
                      <h2>유입 경로별 성과 비교</h2>
                      <p>
                        유입 경로를 선택하면 해당 유입 경로의 전환 성과와 행동
                        분포가 바뀝니다.
                      </p>
                    </div>
                    <label className="a2-sort">
                      정렬
                      <select
                        value={sort}
                        onChange={(e) =>
                          updateQueryParams({ sort: e.target.value })
                        }
                      >
                        <option value="rate">결제 전환율순</option>
                        <option value="revenue">결제금액순</option>
                      </select>
                    </label>
                  </div>
                  <div className="a2-table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>유입 경로</th>
                          <th>가입</th>
                          <th>결제</th>
                          <th>전환율</th>
                          <th>결제금액</th>
                          <th>가입자당</th>
                        </tr>
                      </thead>
                      <tbody>
                        {channelRows.map((r, i) => (
                          <tr
                            key={r.channel}
                            className={
                              filters.channel === r.channel ? "a2-selected" : ""
                            }
                          >
                            <th>
                              <button
                                aria-pressed={filters.channel === r.channel}
                                onClick={() =>
                                  update({
                                    channel:
                                      filters.channel === r.channel
                                        ? "all"
                                        : r.channel,
                                  })
                                }
                              >
                                <span className="a2-rank">{i + 1}</span>
                                {r.channel}
                              </button>
                            </th>
                            <td>{r.count}명</td>
                            <td>{r.buyers}명</td>
                            <td>
                              <b className="a2-rate">{pct(r.rate)}</b>
                              <span className="a2-mini">
                                <i style={{ width: `${r.rate ?? 0}%` }} />
                              </span>
                            </td>
                            <td>{won(r.revenue)}</td>
                            <td>
                              {r.perMember === null ? "—" : won(r.perMember)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="a2-table-note">
                    <span>
                      관찰된 유입 기준 · 직접 응답한 유입 경로는 회원 상세에서
                      별도 확인
                    </span>
                    <span>광고비 미연동 · ROAS 판단 불가</span>
                  </div>
                  <div className="a2-reading">
                    <b>읽는 방법</b>
                    <span>
                      전환율과 가입자당 매출을 함께 비교하세요. 표본이 적거나
                      광고비가 없으면 예산 확대 근거로 단정할 수 없습니다.
                    </span>
                  </div>
                </section>
                <aside className="a2-side">
                  <div className="a2-side-top">
                    <section className="a2-panel a2-behavior">
                      <div className="a2-panel-head">
                        <h2>가입 후 어디까지 했나요?</h2>
                        <span className="a2-chip">{summary.count}명 기준</span>
                      </div>
                      <div className="a2-state-bars">
                        {summary.states.map((s, i) => (
                          <button
                            key={s.label}
                            aria-pressed={status === s.label}
                            onClick={() => {
                              updateVisit({
                                status: status === s.label ? "all" : s.label,
                                lastChannel: filters.channel,
                                tab: "visits",
                              });
                            }}
                          >
                            <span>
                              <i
                                style={{
                                  background: [
                                    "#177361",
                                    "#537bab",
                                    "#8b9fb5",
                                    "#c79b59",
                                    "#a7adb7",
                                    "#d0d7dc",
                                  ][i],
                                }}
                              />
                              {s.label}
                            </span>
                            <div className="a2-state-track">
                              <i
                                style={{
                                  width: `${summary.count ? (s.count / summary.count) * 100 : 0}%`,
                                }}
                              />
                            </div>
                            <b>{s.count}명</b>
                          </button>
                        ))}
                      </div>
                      <p className="a2-small">
                        가장 멀리 진행한 상태로 중복 없이 분류 · 상태 클릭 시
                        마지막 방문 페이지에서 회원 확인
                      </p>
                    </section>
                    <PaymentMixCard
                      mix={overview.data.payment_mix}
                      days={filters.window}
                      channel={filters.channel}
                    />
                  </div>
                  <section className="a2-panel a2-funnel">
                    <div className="a2-panel-head">
                      <h2>신청 → 결제 진행</h2>
                      <span className="a2-chip">선택 유입 경로</span>
                    </div>
                    <div className="a2-funnel-steps">
                      {summary.funnel.map((s, i) => (
                        <div key={s.label}>
                          <strong>
                            {s.count}
                            <small>명</small>
                          </strong>
                          <span>{s.label}</span>
                          <div>
                            <i
                              style={{
                                height: `${summary.funnel[0].count ? (s.count / summary.funnel[0].count) * 100 : 0}%`,
                                opacity: 0.4 + i * 0.2,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="a2-small">
                      단계별 이벤트 필요 · 결제 이후 실제 출원 완료와는 별개
                    </p>
                  </section>
                </aside>
              </div>
              <details className="a2-definitions">
                <summary>집계 기준</summary>
                <p>
                  가입 기간의 회원 중 {filters.window}일 관찰이 끝난 회원만
                  성과에 포함합니다. 유입 경로는 가입 당시 기준이며, 결제금액은
                  환불 전 금액입니다. 가입자당 매출은 미결제 회원을 포함해
                  계산합니다.
                </p>
                <p>
                  단계별 회원 수는 각각 집계하며, 동일 신청 건의 순차 전환을
                  뜻하지 않습니다. 셀프 출원서 저장은 유료 전환에 포함하지
                  않습니다.
                </p>
              </details>
            </>
          )
        ) : (
          <section className="a2-panel a2-members a2-member-workspace">
            <div className="a2-panel-head">
              <div>
                <h2>마지막 방문 페이지</h2>
                <p>페이지 선택 → 회원 선택 → 행동 기록 확인</p>
              </div>
              <div className="a2-member-filters">
                <label>
                  유입 경로
                  <select
                    value={lastChannel}
                    onChange={(e) => {
                      updateVisit({
                        lastChannel: e.target.value,
                        status: null,
                      });
                    }}
                  >
                    <option value="all">전체 유입 경로</option>
                    {channels.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label>
                  행동 유형
                  <select
                    value={status}
                    onChange={(e) => updateVisit({ status: e.target.value })}
                  >
                    <option value="all">관찰 완료 전체</option>
                    {options.behavior_states.map((s) => (
                      <option key={s.label}>{s.label}</option>
                    ))}
                    <option value="pending">관찰 중</option>
                  </select>
                </label>
              </div>
            </div>
            <LastVisitMembers
              key={
                lastChannel +
                filters.period +
                filters.window +
                filters.service +
                status
              }
              options={options}
              params={analyticsParams(filters, options, lastChannel)}
              days={filters.window}
              channel={lastChannel}
              status={status}
              onClearStatus={() => updateVisit({ status: "all" })}
              onClearChannel={() => updateVisit({ lastChannel: null })}
              onExpand={openMember}
            />
          </section>
        )}
      </div>
      <dialog
        ref={dialog}
        className="a2-dialog a2-activity-dialog"
        onClick={(e) => {
          if (e.target === e.currentTarget) dialog.current?.close();
        }}
        onClose={() => {
          if (detailId) updateQueryParams({ detail: null });
        }}
        aria-labelledby="a2-member-title"
      >
        <div className="a2-drawer">
          {member && (
            <>
              <div className="a2-panel-head">
                <h2 id="a2-member-title">{member.name}</h2>
                <button
                  autoFocus
                  className="a2-close"
                  onClick={() => dialog.current?.close()}
                  aria-label="회원 상세 닫기"
                >
                  ×
                </button>
              </div>
              <p className="a2-muted">
                {member.id} · {member.joined} 가입
              </p>
              {detail.loading && <p role="status">회원 정보를 불러오는 중…</p>}
              {detail.error && (
                <p role="alert">
                  {detail.error}{" "}
                  <button onClick={detail.retry}>다시 시도</button>
                </p>
              )}
              <dl className="a2-member-info">
                <div>
                  <dt>가입 시 유입 경로</dt>
                  <dd>{member.channel}</dd>
                </div>
                <div>
                  <dt>직접 응답한 유입 경로</dt>
                  <dd>{member.reported}</dd>
                </div>
                <div>
                  <dt>가입 관심 서비스</dt>
                  <dd>{member.service}</dd>
                </div>
                <div>
                  <dt>선택 관찰 기간 내 상태</dt>
                  <dd>{member.state}</dd>
                </div>
                <div>
                  <dt>관찰 기간</dt>
                  <dd>가입일부터 기준일까지 · 전체 기록</dd>
                </div>
              </dl>
              {
                <ActivityRecord
                  key={member.id + "-" + filters.window}
                  member={member}
                  days={Infinity}
                  asOf={options.latest_as_of}
                  observationDays={filters.window}
                />
              }
              <div className="a2-drawer-note">
                이 기록은 행동을 설명합니다. 후기 부족·가격 불만 등 이탈 원인을
                확정하지 않습니다. 연락처와 수신 동의는 연동되지 않았습니다.
              </div>
            </>
          )}
        </div>
      </dialog>
    </div>
  );
}
